import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Cloud, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmtC = (n) => (n == null ? "—" : "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const fmtD = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const fmtDT = (s) => (s ? new Date(s).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const Stat = ({ label, value, testId, mono = false }) => (
  <div className="border-b border-zinc-100 px-4 py-3">
    <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 mb-0.5">{label}</p>
    <p data-testid={testId} className={`text-sm text-zinc-800 ${mono ? "font-mono" : ""}`}>{value || <span className="text-zinc-300">—</span>}</p>
  </div>
);

const STAGE_CLS = {
  default: "bg-zinc-100 text-zinc-700",
  won: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  lost: "bg-red-50 text-red-700 border border-red-200",
  closed: "bg-zinc-200 text-zinc-700",
  active: "bg-blue-50 text-[#002FA7] border border-blue-200",
};
const stageClass = (sf) => {
  if (!sf) return STAGE_CLS.default;
  if (sf.is_won) return STAGE_CLS.won;
  if (sf.is_closed) return STAGE_CLS.lost;
  return STAGE_CLS.active;
};

export default function SalesforceTab({ oppId, sfDomain }) {
  const [state, setState] = useState({ loading: true, linked: false, sfId: null, data: null, error: null });

  const load = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(`/opportunities/${oppId}/salesforce`);
      setState({ loading: false, linked: !!data.linked, sfId: data.sf_opportunity_id, data: data.data, error: null });
    } catch (err) {
      setState({ loading: false, linked: false, sfId: null, data: null, error: apiError(err) });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [oppId]);

  if (state.loading) {
    return <div className="py-12 text-center text-sm text-zinc-400">Loading from Salesforce…</div>;
  }

  if (!state.linked) {
    return (
      <div className="border border-dashed border-zinc-300 rounded-sm py-16 px-6 text-center">
        <Cloud size={32} strokeWidth={1} className="mx-auto text-zinc-300 mb-4" />
        <p className="text-sm text-zinc-600">This opportunity is not linked to Salesforce.</p>
        <p className="text-xs text-zinc-400 mt-1">Pick an opportunity from the Salesforce search when creating a new one to link it.</p>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-sm p-4">
        <p className="text-sm text-red-700">Salesforce unavailable: {state.error}</p>
        <button onClick={load} className="mt-3 inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline">
          <RefreshCw size={11} strokeWidth={1.5} /> Retry
        </button>
      </div>
    );
  }

  const sf = state.data;
  if (!sf) {
    return <p className="text-sm text-zinc-500">Salesforce record not found for ID <span className="font-mono">{state.sfId}</span> (it may have been deleted).</p>;
  }
  const sfUrl = sfDomain ? `${sfDomain.replace(/\/$/, "")}/${sf.id}` : null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Cloud size={18} strokeWidth={1.5} className="text-[#002FA7]" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Linked Salesforce opportunity</p>
            <p className="text-base font-medium tracking-tight">{sf.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="sf-stage-badge" className={`text-[10px] uppercase tracking-wide font-medium rounded-sm px-2.5 py-1 ${stageClass(sf)}`}>
            {sf.stage || "—"}
          </span>
          <button onClick={load} className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#002FA7]" data-testid="sf-refresh-button">
            <RefreshCw size={12} strokeWidth={1.5} /> Refresh
          </button>
          {sfUrl && (
            <a href={sfUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#002FA7] hover:underline" data-testid="sf-open-link">
              <ExternalLink size={12} strokeWidth={1.5} /> Open in Salesforce
            </a>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200 rounded-sm overflow-hidden">
        <div className="bg-white">
          <Stat label="Stage" value={sf.stage} testId="sf-stage" />
          <Stat label="Probability" value={sf.probability != null ? `${sf.probability}%` : null} mono testId="sf-probability" />
          <Stat label="Forecast category" value={sf.forecast_category} testId="sf-forecast" />
          <Stat label="Type" value={sf.type} testId="sf-type" />
          <Stat label="Lead source" value={sf.lead_source} testId="sf-lead-source" />
          <Stat label="Owner" value={sf.owner_name} testId="sf-owner" />
          <Stat label="Account" value={sf.account_name} testId="sf-account" />
        </div>
        <div className="bg-white">
          <Stat label="Amount" value={fmtC(sf.amount)} mono testId="sf-amount" />
          <Stat label="Expected revenue" value={fmtC(sf.expected_revenue)} mono testId="sf-expected-revenue" />
          <Stat label="Close date" value={fmtD(sf.close_date)} testId="sf-close-date" />
          <Stat label="Next step" value={sf.next_step} testId="sf-next-step" />
          <Stat label="Created" value={fmtDT(sf.created_date)} mono testId="sf-created" />
          <Stat label="Last modified" value={fmtDT(sf.last_modified_date)} mono testId="sf-modified" />
          <Stat label="Salesforce Id" value={sf.id} mono testId="sf-id" />
        </div>
      </div>

      {sf.description && (
        <div className="bg-white border border-zinc-200 rounded-sm p-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Description</p>
          <p className="text-sm text-zinc-700 whitespace-pre-wrap">{sf.description}</p>
        </div>
      )}
    </div>
  );
}
